import React, { useState, useCallback } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Slider,
  Stack,
  createTheme,
  ThemeProvider
} from '@mui/material';
import {
  Percent as PercentIcon,
  ProductionQuantityLimits as QtyIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const customTheme = createTheme({
  palette: {
    primary: {
      main: '#4F46E5',
    },
    secondary: {
      main: '#10B981',
    },
    error: {
      main: '#EF4444',
    },
  },
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 550,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 3,
  outline: 'none',
};

const DefaultValueManagerModal = ({defaultValuePopUp, setDefaultValuePopUp, defaultValue, setDefaultValue}) => {

  const [errors, setErrors] = useState({});

  const handleClose = useCallback(() => setDefaultValuePopUp(false), [setDefaultValuePopUp]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    let newValue = parseInt(value) || 0;
    setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'qty' && newValue < 1) {
      setErrors(prev => ({ ...prev, qty: 'Quantity must be 1 or higher' }));
    }
    setDefaultValue(prev => ({ ...prev, [name]: newValue }));
  }, [setDefaultValue]);

  const handleSliderChange = useCallback((e, newValue) => {
    setDefaultValue(prev => ({ ...prev, discount_per: newValue }));
  }, [setDefaultValue]);

  const handleSave = useCallback(() => {
    if (defaultValue.qty < 1) {
      setErrors({ qty: 'Quantity must be 1 or higher to save.' });
      return;
    }
    console.log('Default Values Saved:', defaultValue);
    handleClose();
  }, [defaultValue, handleClose]);

  return (
    <ThemeProvider theme={customTheme}>
      <Modal open={defaultValuePopUp} onClose={handleClose}>
        <Box sx={modalStyle} className="rounded-xl shadow-2xl">
          
          <Stack direction="row" justifyContent="space-between" alignItems="center" 
              className="mb-4 pb-2 border-b border-gray-100">
              <Typography id="default-value-modal-title" variant="h5" component="h2" 
                  className="font-bold text-gray-800 flex items-center">
                  ⚙️ Manage Default Values
              </Typography>
              <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" size="small">Close</Button>
          </Stack>

          <div className="mb-6 p-3 border border-indigo-100 rounded-lg bg-indigo-50">
              <Typography variant="subtitle1" gutterBottom 
                  className="font-semibold text-indigo-700 flex items-center mb-2">
                  <QtyIcon fontSize="small" className="mr-2" />
                  Default Quantity
              </Typography>
              <TextField
                  name="qty"
                  label="Starting Quantity"
                  type="number"
                  value={defaultValue.qty}
                  onChange={handleChange}
                  error={!!errors.qty}
                  helperText={errors.qty || "The minimum quantity is 1."}
                  InputProps={{ inputProps: { min: 1 } }}
              />
          </div>

          <div className="mb-6 p-3 border border-emerald-100 rounded-lg bg-emerald-50">
              <Typography variant="subtitle1" gutterBottom 
                  className="font-semibold text-emerald-700 flex items-center mb-4">
                  <PercentIcon fontSize="small" className="mr-2" />
                  Default Discount Percentage: <span className="ml-2 font-extrabold text-xl">{defaultValue.discount_per}%</span>
              </Typography>
              <Slider
                  name="discount_per"
                  value={defaultValue.discount_per}
                  onChange={handleSliderChange}
                  valueLabelDisplay="auto" 
                  step={1}
                  marks
                  min={0}
                  max={100}
                  color="secondary"
                  className="mt-6"
              />
          </div>

          <Stack direction="row" spacing={2} justifyContent="flex-end" className="pt-4 border-t border-gray-100">
            <Button onClick={handleClose} variant="outlined" color="primary">
              Close
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={!!errors.qty}
            >
              Save Defaults
            </Button>
          </Stack>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default DefaultValueManagerModal;
